import { useEffect, useState } from "react";
import { Images, X } from "lucide-react";
import { API, imageRatioClass, imgSrc } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export default function Gallery() {
  const [photos, setPhotos] = useState(null);
  const [selected, setSelected] = useState(null);

  usePageMeta("Galeri Produk", "Kumpulan foto produk dan aktivitas NAWADAH by NDH Official.");

  useEffect(() => {
    API.get("/gallery").then((response) => setPhotos(response.data || [])).catch(() => setPhotos([]));
  }, []);

  useEffect(() => {
    if (!selected) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelected(null);
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selected]);

  return (
    <div className="min-h-screen bg-navy-dark text-white">
      <section className="relative border-b border-gold/20 bg-maritime-grid py-20 noise-overlay" data-testid="gallery-header">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at 15% 0%, rgba(201,162,39,0.12) 0%, transparent 55%)" }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">Galeri Foto</p>
          <h1 className="font-display text-4xl tracking-tight sm:text-5xl">Galeri NAWADAH</h1>
          <p className="mt-4 max-w-2xl text-sm text-slate-400 sm:text-base">
            Lihat koleksi foto produk, kegiatan, dan informasi terbaru dari NAWADAH by NDH Official.
          </p>
        </div>
      </section>

      <section className="relative bg-rope-pattern py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          {photos === null && (
            <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4" aria-label="Memuat galeri">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="aspect-square animate-pulse rounded-md bg-white/5" />
              ))}
            </div>
          )}

          {photos && photos.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4" data-testid="gallery-grid">
              {photos.map((photo) => (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => setSelected(photo)}
                  className="group overflow-hidden rounded-md border border-white/10 bg-white/[0.03] text-left transition hover:-translate-y-0.5 hover:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold"
                  data-testid={`gallery-card-${photo.id}`}
                  aria-label={`Buka foto ${photo.title || "Galeri NAWADAH"}`}
                >
                  <div className={`${imageRatioClass(photo.image_ratio, "1:1")} overflow-hidden bg-black/20`}>
                    <img
                      src={imgSrc(photo.image_url)}
                      alt={photo.title || "Foto Galeri NAWADAH"}
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>
                  {(photo.title || photo.description) && (
                    <div className="p-3 sm:p-4">
                      {photo.title && <h2 className="line-clamp-2 font-display text-sm text-white sm:text-base">{photo.title}</h2>}
                      {photo.description && <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-400">{photo.description}</p>}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {photos && photos.length === 0 && (
            <div className="rounded-md border border-white/10 bg-white/[0.03] px-6 py-14 text-center">
              <Images className="mx-auto h-9 w-9 text-gold" />
              <p className="mt-4 text-sm text-slate-400">Foto galeri akan segera hadir.</p>
            </div>
          )}
        </div>
      </section>

      {selected && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-3 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={selected.title || "Foto Galeri NAWADAH"}
          onClick={() => setSelected(null)}
        >
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-gold"
            aria-label="Tutup foto"
          >
            <X className="h-6 w-6" />
          </button>
          <figure className="max-h-full max-w-6xl" onClick={(event) => event.stopPropagation()}>
            <img
              src={imgSrc(selected.image_url)}
              alt={selected.title || "Foto Galeri NAWADAH"}
              className="max-h-[80vh] w-auto max-w-full rounded-md object-contain"
            />
            {(selected.title || selected.description) && (
              <figcaption className="mx-auto max-w-3xl pt-4 text-center">
                {selected.title && <p className="font-display text-lg text-white">{selected.title}</p>}
                {selected.description && <p className="mt-1 text-sm text-slate-300">{selected.description}</p>}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </div>
  );
}
